import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate } from 'react-router-dom'
import { Eye, FilterX, Mail, MoreHorizontal, Plus, Search, UserCheck, UserX, Users, UsersRound } from 'lucide-react'
import { ApiError } from '../../../lib/api'
import { getAppLocale } from '@/lib/locale'
import { useAuthSession } from '../../auth/session'
import { fetchAgents, inviteAgent, reactivateAgent, suspendAgent } from '../api'
import { AddAgentDialog } from '../components/AddAgentDialog'
import { AgentLifecycleConfirmDialog, type AgentLifecycleAction } from '../components/AgentActionDialogs'
import { Field, FieldLabel } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { KpiCard, KpiCardSkeleton } from '@/features/dashboard/components/KpiCard'
import { DashboardSectionHeader } from '@/features/dashboard/components/DashboardSectionHeader'
import {
  invalidateOwnerRefreshQueries,
  useOwnerRefreshPolicy,
} from '@/features/dashboard/owner-refresh'
import {
  agentStatusBadgeClass,
  formatDashboardDateFr,
  programStatusBadgeClass,
} from '@/features/dashboard/utils/semanticBadges'
import { DetailEmptyState } from '@/components/app/DetailPageKit'
import { PageHeader, PageHeaderToolbar } from '@/components/app/PageHeader'
import { SortableTableHead, type SortDirection } from '@/components/app/SortableTableHead'
import { StateSurface, StateSurfaceIconAsset } from '@/components/app/StateSurface'
import { TablePaginationBar } from '@/components/app/TablePaginationBar'
import { AgentAvatarFallback, Avatar, AvatarImage } from '@/components/ui/avatar'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Skeleton } from '@/components/ui/skeleton'
import { IconActionButton } from '@/components/ui/icon-action-button'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import type { AgentListEnvelope, AgentRecord } from '@/types/agents'

type AgentSortKey = 'agent' | 'status' | 'joined'

function initials(name: string) {
  const parts = name.trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return '?'
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
  return `${parts[0][0] ?? ''}${parts[parts.length - 1][0] ?? ''}`.toUpperCase()
}

function agentAffiliateSubline(agent: { id: string; agent_code: string | null }) {
  if (agent.agent_code?.trim()) return agent.agent_code
  return 'Code non attribué'
}

function normalizeAgentStatus(status: string) {
  return status.toLowerCase().replace(/\s+/g, '_')
}

function getAgentStatusLabel(agent: AgentRecord, t: (key: string) => string) {
  const normalizedStatus = normalizeAgentStatus(agent.status)

  if (normalizedStatus === 'invited' || normalizedStatus === 'pending' || normalizedStatus === 'pending_activation') {
    return agent.activated_at ? t('agents.status.pending') : t('agents.status.pendingActivation')
  }

  if (normalizedStatus === 'suspended' || normalizedStatus === 'inactive') {
    return t('agents.status.suspended')
  }

  if (normalizedStatus === 'active') {
    return t('agents.status.active')
  }

  return t('common.unknown')
}

function agentJoinedTime(agent: AgentRecord) {
  return new Date(agent.activated_at ?? agent.invited_at ?? agent.created_at ?? 0).getTime()
}

function compareAgents(left: AgentRecord, right: AgentRecord, key: AgentSortKey, direction: SortDirection) {
  const modifier = direction === 'asc' ? 1 : -1
  const result =
    key === 'joined'
      ? agentJoinedTime(left) - agentJoinedTime(right)
      : key === 'status'
        ? normalizeAgentStatus(left.status).localeCompare(normalizeAgentStatus(right.status))
        : (left.display_name ?? left.email ?? '').localeCompare(right.display_name ?? right.email ?? '')

  return result * modifier
}

function AgentsPageSkeleton() {
  return (
    <section className="app-section">
      <PageHeader
        title={<Skeleton className="h-6 w-24" />}
        right={<Skeleton className="h-9 w-28 rounded-md" />}
      />

      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        {Array.from({ length: 4 }).map((_, index) => (
          <KpiCardSkeleton key={index} />
        ))}
      </div>

      <article className="rounded-lg bg-card p-3 sm:p-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="space-y-2">
            <Skeleton className="h-5 w-24" />
            <Skeleton className="h-4 w-64 max-w-full" />
          </div>
          <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row">
            <Skeleton className="h-9 w-full sm:w-[260px]" />
            <Skeleton className="h-9 w-full sm:w-[150px]" />
          </div>
        </div>

        <div className="mt-4 overflow-hidden rounded-lg bg-background/40">
          <div className="h-11 bg-muted/30" />
          {Array.from({ length: 6 }).map((_, index) => (
            <div key={index} className="h-16 border-t border-border/50 bg-card/70 first:border-t-0" />
          ))}
        </div>
      </article>
    </section>
  )
}

export function AgentsPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { hasPermission } = useAuthSession()
  const canInvite = hasPermission('agent.invite')
  const canSuspend = hasPermission('agent.suspend')
  const canReactivate = hasPermission('agent.reactivate')
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'pending' | 'suspended'>('all')
  const [inviteOpen, setInviteOpen] = useState(false)
  const [pendingLifecycleAction, setPendingLifecycleAction] = useState<{
    type: AgentLifecycleAction
    agent: AgentRecord
  } | null>(null)
  const [programsDialogAgent, setProgramsDialogAgent] = useState<AgentRecord | null>(null)
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [sortKey, setSortKey] = useState<AgentSortKey | null>(null)
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc')
  const { queryOptions, triggerBurst } = useOwnerRefreshPolicy(true)

  const listQuery = useQuery<AgentListEnvelope>({
    queryKey: ['agents', 'list'],
    queryFn: fetchAgents,
    refetchOnWindowFocus: queryOptions.refetchOnWindowFocus,
    refetchInterval: queryOptions.refetchInterval,
  })

  const inviteMutation = useMutation({
    mutationFn: inviteAgent,
    onSuccess: async () => {
      setInviteOpen(false)
      triggerBurst()
      await invalidateOwnerRefreshQueries(queryClient)
      await queryClient.refetchQueries({ queryKey: ['dashboard', 'business-summary'] })
    },
  })

  const suspendMutation = useMutation({
    mutationFn: ({ agentId, reason }: { agentId: string; reason: string }) => suspendAgent(agentId, { reason }),
    onSuccess: async () => {
      triggerBurst()
      await invalidateOwnerRefreshQueries(queryClient)
      setPendingLifecycleAction(null)
    },
  })

  const reactivateMutation = useMutation({
    mutationFn: ({ agentId }: { agentId: string }) => reactivateAgent(agentId),
    onSuccess: async () => {
      triggerBurst()
      await invalidateOwnerRefreshQueries(queryClient)
      setPendingLifecycleAction(null)
    },
  })

  const records = listQuery.data?.data ?? []

  const agentKpis = useMemo(() => {
    let active = 0
    let pendingInvite = 0
    let suspended = 0
    for (const a of records) {
      const s = normalizeAgentStatus(a.status)
      if (s === 'active') active += 1
      else if (s === 'invited' || s === 'pending' || s === 'pending_activation') pendingInvite += 1
      else if (s === 'suspended' || s === 'inactive') suspended += 1
    }
    return {
      total: records.length,
      active,
      pendingInvite,
      suspended,
    }
  }, [records])

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    return records.filter((item) => {
      const matchSearch = !q || [item.display_name ?? '', item.email ?? '', item.agent_code ?? ''].some(value => value.toLowerCase().includes(q))
      
      const s = normalizeAgentStatus(item.status)
      let mappedStatus = s
      if (s === 'invited' || s === 'pending' || s === 'pending_activation') mappedStatus = 'pending'
      else if (s === 'inactive' || s === 'suspended') mappedStatus = 'suspended'
      
      const matchStatus = statusFilter === 'all' || mappedStatus === statusFilter
      return matchSearch && matchStatus
    })
  }, [records, search, statusFilter])

  const hasActiveFilters = search.trim().length > 0 || statusFilter !== 'all'

  const sortedAgents = useMemo(() => {
    if (!sortKey) return filtered
    return [...filtered].sort((left, right) => compareAgents(left, right, sortKey, sortDirection))
  }, [filtered, sortDirection, sortKey])

  function handleSort(nextKey: AgentSortKey) {
    setPage(1)
    if (sortKey === nextKey) {
      setSortDirection((current) => (current === 'asc' ? 'desc' : 'asc'))
      return
    }
    setSortKey(nextKey)
    setSortDirection(nextKey === 'joined' ? 'desc' : 'asc')
  }

  useEffect(() => {
    setPage(1)
  }, [search])

  const totalFiltered = sortedAgents.length
  const totalPages = Math.max(1, Math.ceil(totalFiltered / pageSize))
  const pageSafe = Math.min(page, totalPages)
  const pageSlice = useMemo(() => {
    const start = (pageSafe - 1) * pageSize
    return sortedAgents.slice(start, start + pageSize)
  }, [sortedAgents, pageSafe, pageSize])

  useEffect(() => {
    if (page !== pageSafe) setPage(pageSafe)
  }, [page, pageSafe])

  if (listQuery.isPending) {
    return <AgentsPageSkeleton />
  }

  if (listQuery.isError) {
    return (
      <article className="rounded-lg border border-red-200 bg-red-50 p-6 text-sm text-red-700">
        {(listQuery.error as ApiError).message}
      </article>
    )
  }

  return (
    <>
      <AddAgentDialog
        open={inviteOpen}
        isPending={inviteMutation.isPending}
        error={inviteMutation.isError ? (inviteMutation.error as ApiError) : null}
        onClose={() => {
          setInviteOpen(false)
          inviteMutation.reset()
        }}
        onSubmit={(payload) => inviteMutation.mutate(payload)}
      />
      <AgentLifecycleConfirmDialog
        action={pendingLifecycleAction}
        isSubmitting={suspendMutation.isPending || reactivateMutation.isPending}
        error={
          suspendMutation.isError
            ? (suspendMutation.error as ApiError)
            : reactivateMutation.isError
              ? (reactivateMutation.error as ApiError)
              : null
        }
        onClose={() => {
          setPendingLifecycleAction(null)
          suspendMutation.reset()
          reactivateMutation.reset()
        }}
        onConfirm={async (action, agent, payload) => {
          if (action === 'suspend') {
            await suspendMutation.mutateAsync({ agentId: agent.id, reason: payload.reason ?? '' })
            return
          }
          await reactivateMutation.mutateAsync({ agentId: agent.id })
        }}
      />
      <Dialog open={programsDialogAgent !== null} onOpenChange={(open) => !open && setProgramsDialogAgent(null)}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>{t('agents.dialogs.assignedPrograms')}</DialogTitle>
            <DialogDescription>
              {programsDialogAgent
                ? t('agents.dialogs.assignedProgramsDescription')
                : t('agents.dialogs.assignedProgramsDescription')}
            </DialogDescription>
          </DialogHeader>
          {programsDialogAgent && (programsDialogAgent.assigned_programs?.length ?? 0) > 0 ? (
            <div className="space-y-3">
              {programsDialogAgent.assigned_programs?.map((assignment) => (
                <button
                  key={assignment.assignment_id}
                  type="button"
                  className="flex w-full cursor-pointer items-center justify-between rounded-lg border border-dashed border-border bg-background px-4 py-3 text-left transition-colors hover:border-border hover:bg-muted/20"
                  onClick={() => {
                    setProgramsDialogAgent(null)
                    navigate(`/programs/${assignment.program.id}`)
                  }}
                >
                  <div className="min-w-0">
                    <p className="truncate font-medium text-foreground">{assignment.program.name}</p>
                    <p className="text-sm text-muted-foreground">
                      {t('agents.dialogs.assignedOn')} {formatDashboardDateFr(assignment.assigned_at)}
                    </p>
                  </div>
                  <div className="ml-4 flex shrink-0 items-center gap-2">
                    <Badge variant="secondary" className="whitespace-nowrap">
                      {(assignment.program.assigned_agents_count ?? 0).toLocaleString(getAppLocale())} {t('agents.table.programs_plural')}
                    </Badge>
                    <Badge className={programStatusBadgeClass(assignment.program.status)}>
                      {t('programs.status.' + assignment.program.status)}
                    </Badge>
                  </div>
                </button>
              ))}
            </div>
          ) : (
            <DetailEmptyState
              message={t('agents.dialogs.noPrograms')}
            />
          )}
        </DialogContent>
      </Dialog>
      <section className="app-section">
      <PageHeader
        title={t('agents.title')}
        right={
          <PageHeaderToolbar>
            {hasActiveFilters ? (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      className="shrink-0 text-destructive border-destructive/30 hover:bg-destructive/10 hover:text-destructive"
                      onClick={() => {
                        setSearch('')
                        setStatusFilter('all')
                      }}
                      aria-label={t('common.clearFilters')}
                    >
                      <FilterX className="size-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>{t('common.clearFilters')}</TooltipContent>
                </Tooltip>
              </TooltipProvider>
            ) : null}
            <Field className="w-full sm:w-[240px] shrink-0">
              <FieldLabel htmlFor="agents-search" className="sr-only">
                {t('agents.searchPlaceholder')}
              </FieldLabel>
              <div className="relative">
                <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  id="agents-search"
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder={t('agents.searchPlaceholder')}
                  className="pl-9"
                />
              </div>
            </Field>
            <Select
              value={statusFilter}
              onValueChange={(value: 'all' | 'active' | 'pending' | 'suspended') => setStatusFilter(value)}
            >
              <SelectTrigger className="w-full sm:w-[160px] shrink-0">
                <SelectValue placeholder={t('common.status')} />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  <SelectLabel>{t('common.status')}</SelectLabel>
                  <SelectItem value="all">{t('agents.filters.allStatuses')}</SelectItem>
                  <SelectItem value="active">{t('agents.status.active')}</SelectItem>
                  <SelectItem value="pending">{t('agents.status.pending')}</SelectItem>
                  <SelectItem value="suspended">{t('agents.status.suspended')}</SelectItem>
                </SelectGroup>
              </SelectContent>
            </Select>
            {canInvite ? (
              <Button
                type="button"
                className="gap-2 shrink-0"
                data-onboarding-target="agent"
                onClick={() => setInviteOpen(true)}
              >
                <Plus className="size-4" aria-hidden />
                {t('agents.addAgent')}
              </Button>
            ) : null}
          </PageHeaderToolbar>
        }
      />

        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 xl:grid-cols-4">
          <KpiCard
            title={t('agents.kpis.total')}
            value={agentKpis.total.toLocaleString(getAppLocale())}
            description={t('agents.kpis.totalDescription')}
            icon={Users}
            tone="primary"
          />
          <KpiCard
            title={t('agents.kpis.active')}
            value={agentKpis.active.toLocaleString(getAppLocale())}
            description={t('agents.kpis.activeDescription')}
            icon={UserCheck}
            tone="success"
          />
          <KpiCard
            title={t('agents.kpis.pending')}
            value={agentKpis.pendingInvite.toLocaleString(getAppLocale())}
            description={t('agents.kpis.pendingDescription')}
            icon={Mail}
            tone="info"
          />
          <KpiCard
            title={t('agents.kpis.suspended')}
            value={agentKpis.suspended.toLocaleString(getAppLocale())}
            description={t('agents.kpis.suspendedDescription')}
            icon={UserX}
            tone="warning"
          />
        </div>

        <article className="rounded-lg bg-card p-3 sm:p-4">
          <DashboardSectionHeader
            title={t('agents.title')}
            description={
              agentKpis.pendingInvite > 0
                ? t('agents.pendingInvitesHint', { count: agentKpis.pendingInvite })
                : t('agents.description')
            }

          />

          {totalFiltered === 0 ? (
            <StateSurface
              variant="section"
              size="md"
              asset={<StateSurfaceIconAsset icon={UsersRound} size="md" />}
              title={t('agents.empty.title')}
              description={t('agents.empty.description')}
              primaryAction={
                hasActiveFilters
                  ? {
                      label: t('common.clearFilters'),
                      onClick: () => {
                        setSearch('')
                        setStatusFilter('all')
                      },
                      size: 'default',
                    }
                  : canInvite
                    ? {
                        label: t('agents.empty.cta'),
                        onClick: () => setInviteOpen(true),
                        size: 'default',
                      }
                    : undefined
              }
            />
          ) : (
            <div className="overflow-hidden rounded-lg border border-border">
              <div className="overflow-x-auto">
                <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10 text-center">#</TableHead>
                  <SortableTableHead
                    sortKey="agent"
                    activeKey={sortKey}
                    direction={sortDirection}
                    onSort={handleSort}
                  >
                    {t('agents.table.agent')}
                  </SortableTableHead>
                  <TableHead className="hidden sm:table-cell">{t('agents.table.email')}</TableHead>
                  <SortableTableHead
                    sortKey="status"
                    activeKey={sortKey}
                    direction={sortDirection}
                    onSort={handleSort}
                    className="hidden md:table-cell"
                  >
                    {t('agents.table.status')}
                  </SortableTableHead>
                  <TableHead className="hidden lg:table-cell">{t('agents.table.programs')}</TableHead>
                  <SortableTableHead
                    sortKey="joined"
                    activeKey={sortKey}
                    direction={sortDirection}
                    onSort={handleSort}
                  >
                    {t('agents.table.joined')}
                  </SortableTableHead>
                  <TableHead className="w-10 pe-2 text-end">
                    <span className="sr-only">{t('common.actions')}</span>
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {pageSlice.map((agent, index) => {
                  const displayName = agent.display_name ?? t('agents.table.untitled')
                  const joinedAt = agent.activated_at ?? agent.invited_at ?? agent.created_at ?? null
                  const actionsBusy = suspendMutation.isPending || reactivateMutation.isPending
                  const showSuspend = agent.status !== 'suspended' && canSuspend
                  const showReactivate = agent.status === 'suspended' && canReactivate
                  const rank = (pageSafe - 1) * pageSize + index + 1
                  return (
                    <TableRow key={agent.id}>
                      <TableCell className="text-center text-muted-foreground">{rank}</TableCell>
                      <TableCell>
                        <Link
                          to={`/agents/${agent.id}`}
                          className="group -m-1 flex min-w-0 items-center gap-2.5 rounded-md p-1 text-left outline-none transition-colors hover:bg-muted/60 focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                          aria-label={`${t('agents.table.actions.viewProfile')} ${displayName}`}
                        >
                          <Avatar className="size-9 shrink-0">
                            <AvatarImage src={agent.avatar_url ?? undefined} alt={displayName} />
                            <AgentAvatarFallback seed={agent.id} className="text-xs font-medium">
                              {initials(displayName)}
                            </AgentAvatarFallback>
                          </Avatar>
                          <div className="min-w-0">
                            <p className="truncate font-medium text-primary underline underline-offset-4 decoration-border group-hover:decoration-primary">
                              {displayName}
                            </p>
                            <p className="truncate font-mono text-[11px] text-muted-foreground">
                              {agentAffiliateSubline(agent)}
                            </p>
                          </div>
                        </Link>
                      </TableCell>
                      <TableCell className="hidden max-w-[12rem] truncate sm:table-cell">
                        {agent.email ?? t('common.notAvailable')}
                      </TableCell>
                      <TableCell className="hidden md:table-cell">
                        {agent.status ? (
                          <Badge
                            variant="outline"
                            className={`capitalize ${agentStatusBadgeClass(agent.status)}`}
                          >
                            {getAgentStatusLabel(agent, t)}
                          </Badge>
                        ) : (
                          t('common.notAvailable')
                        )}
                      </TableCell>
                      <TableCell className="hidden lg:table-cell">
                        <button
                          type="button"
                          className="cursor-pointer"
                          onClick={() => setProgramsDialogAgent(agent)}
                        >
                          <Badge
                            variant="secondary"
                            className="transition-colors hover:bg-secondary/80"
                          >
                            {(agent.assigned_programs_count ?? 0).toLocaleString(getAppLocale())}{' '}
                            {(agent.assigned_programs_count ?? 0) > 1 ? t('agents.table.programs_plural') : t('agents.table.program')}
                          </Badge>
                        </button>
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {formatDashboardDateFr(joinedAt)}
                      </TableCell>
                      <TableCell className="pe-2 text-end">

                          <div className="flex justify-end gap-2">
                            <div className="hidden items-center justify-end gap-2 md:flex">
                              <IconActionButton
                                icon={<Eye className="size-4" />}
                                tooltip={t('agents.table.actions.viewProfile')}
                                to={`/agents/${agent.id}`}
                              />
                              {showSuspend ? (
                                <IconActionButton
                                  icon={<UserX className="size-4" />}
                                  tooltip={t('agents.table.actions.suspend')}
                                  tone="danger"
                                  onClick={() => setPendingLifecycleAction({ type: 'suspend', agent })}
                                  disabled={actionsBusy}
                                />
                              ) : null}
                              {showReactivate ? (
                                <IconActionButton
                                  icon={<UserCheck className="size-4" />}
                                  tooltip={t('agents.table.actions.reactivate')}
                                  tone="success"
                                  onClick={() => setPendingLifecycleAction({ type: 'reactivate', agent })}
                                  disabled={actionsBusy}
                                />
                              ) : null}
                            </div>
                            <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon-sm"
                              className="text-muted-foreground md:hidden"
                              aria-label={t('common.actions')}
                            >
                              <MoreHorizontal className="size-4" aria-hidden />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="min-w-[10rem]">
                            <DropdownMenuItem asChild>
                              <Link to={`/agents/${agent.id}`}>
                                <Eye className="size-4 text-primary" />
                                <span>{t('agents.table.actions.viewProfile')}</span>
                              </Link>
                            </DropdownMenuItem>
                            {(showSuspend || showReactivate) && (
                              <DropdownMenuSeparator />
                            )}
                            {showSuspend ? (
                              <DropdownMenuItem
                                variant="destructive"
                                disabled={actionsBusy}
                                onClick={() => setPendingLifecycleAction({ type: 'suspend', agent })}
                              >
                                <UserX className="size-4 text-destructive" />
                                <span>{t('agents.table.actions.suspend')}</span>
                              </DropdownMenuItem>
                            ) : null}
                            {showReactivate ? (
                              <DropdownMenuItem
                                disabled={actionsBusy}
                                className="text-emerald-600 focus:text-emerald-600 dark:text-emerald-400 dark:focus:text-emerald-400"
                                onClick={() => setPendingLifecycleAction({ type: 'reactivate', agent })}
                              >
                                <UserCheck className="size-4 text-emerald-500" />
                                <span>{t('agents.table.actions.reactivate')}</span>
                              </DropdownMenuItem>
                            ) : null}
                          </DropdownMenuContent>
                        </DropdownMenu>
                          </div>

                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
              </div>
              <TablePaginationBar
                page={pageSafe}
                pageSize={pageSize}
                totalItems={totalFiltered}
                onPageChange={setPage}
                onPageSizeChange={setPageSize}
                pageSizeOptions={[10, 25, 50, 100]}
              />
            </div>
          )}
        </article>
      </section>
    </>
  )
}
